/**
 * SlipTrackerAuditPage — /books-analytics/slip-tracker-audit
 * ─────────────────────────────────────────────────────────
 * Full audit trail between two Slip Tracker snapshots for the same custom
 * date range. Mirrors Slip Tracker's filter surface (Hide-0-due, Include
 * overdue, Exclude CT, Exclude SP, Group by) so numbers are apples-to-apples.
 * Every invoice is classified into Left / Entered / Changed / Unchanged and
 * every LEFT / CHANGED row shows its reason + evidence (payments, credit
 * notes, Zoho comment timeline with who/when).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import SlipInvoiceDetailModal from './SlipInvoiceDetailModal';
import ExecutiveSummaryModal from './ExecutiveSummaryModal';
import Tour from './Tour';
import {
  ChevronDown, ChevronRight, Loader2, RefreshCw, Download, PlayCircle,
  Wallet, Receipt, CalendarClock, Ban, HelpCircle, ArrowRight,
  UserRound, Sparkles, LogIn, LogOut, Search, ExternalLink, X, FileText, Info,
} from 'lucide-react';

const fmtInr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const fmtDelta = (n) => (n > 0 ? '+' : '') + fmtInr(n);
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }); }
  catch { return iso; }
};

const REASON_META = {
  payment_received:         { label: 'Payment received',      cls: 'bg-emerald-100 text-emerald-700 border-emerald-300', Icon: Wallet },
  credit_note_applied:      { label: 'Credit note applied',   cls: 'bg-violet-100 text-violet-700 border-violet-300',   Icon: Receipt },
  settled_mixed:            { label: 'Cleared (Payment + CN)', cls: 'bg-teal-100 text-teal-700 border-teal-300',        Icon: Wallet },
  balance_cleared:          { label: 'Cleared',               cls: 'bg-emerald-100 text-emerald-700 border-emerald-300', Icon: Wallet },
  balance_reduced_other:    { label: 'Balance dropped',       cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: Wallet },
  expected_date_moved:      { label: 'Promise date shifted',  cls: 'bg-amber-100 text-amber-700 border-amber-300',      Icon: CalendarClock },
  expected_date_moved_in:   { label: 'Pulled into range',     cls: 'bg-sky-100 text-sky-700 border-sky-300',            Icon: LogIn },
  expected_date_moved_out:  { label: 'Pushed out of range',   cls: 'bg-orange-100 text-orange-700 border-orange-300',   Icon: LogOut },
  reassigned_salesperson:   { label: 'Salesperson changed',   cls: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-300', Icon: UserRound },
  reclassified_client_type: { label: 'Client type changed',   cls: 'bg-indigo-100 text-indigo-700 border-indigo-300',   Icon: UserRound },
  reassigned_out_of_scope:  { label: 'Salesperson excluded',  cls: 'bg-slate-100 text-slate-600 border-slate-300',      Icon: UserRound },
  reassigned_into_scope:    { label: 'Salesperson re-included', cls: 'bg-slate-100 text-slate-600 border-slate-300',    Icon: UserRound },
  reclassified_out_of_scope:{ label: 'Client type excluded',  cls: 'bg-slate-100 text-slate-600 border-slate-300',      Icon: UserRound },
  reclassified_into_scope:  { label: 'Client type re-included', cls: 'bg-slate-100 text-slate-600 border-slate-300',    Icon: UserRound },
  invoice_voided:           { label: 'Invoice voided',        cls: 'bg-rose-100 text-rose-700 border-rose-300',         Icon: Ban },
  write_off:                { label: 'Written off',           cls: 'bg-orange-100 text-orange-700 border-orange-300',   Icon: Sparkles },
  new_invoice:              { label: 'Newly created',         cls: 'bg-sky-100 text-sky-700 border-sky-300',            Icon: LogIn },
  entered_scope:            { label: 'Newly in range',        cls: 'bg-sky-100 text-sky-700 border-sky-300',            Icon: LogIn },
  removed_from_universe:    { label: 'No longer in books',    cls: 'bg-slate-100 text-slate-600 border-slate-300',      Icon: Ban },
  unknown:                  { label: 'Other',                 cls: 'bg-slate-100 text-slate-500 border-slate-300',      Icon: HelpCircle },
  unchanged:                { label: 'No change',             cls: 'bg-slate-50 text-slate-400 border-slate-200',       Icon: HelpCircle },
};

const yesterday = (iso) => {
  const d = iso ? new Date(iso + 'T00:00:00') : new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};
// Business date is IST. Deployed backend runs UTC, and browsers in some setups
// may not be IST either — normalise "today" via en-CA locale in Asia/Kolkata so
// the audit's default dates always match India's clock.
const todayISO = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

const PRESETS = (from, to) => {
  const t = todayISO();
  const [y, m] = t.split('-').map(Number);
  const eom = new Date(y, m, 0).getDate();
  const dISO = (d) => `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const addDays = (n) => { const d = new Date(t + 'T00:00:00'); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
  return [
    { k: 'today',   label: 'Today',    from: t, to: t },
    { k: 'nxt7',    label: 'Next 7d',  from: t, to: addDays(7) },
    { k: 'nxt10',   label: 'Next 10d', from: t, to: addDays(10) },
    { k: 'w1',      label: '1–10',     from: dISO(1),  to: dISO(Math.min(10, eom)) },
    { k: 'w2',      label: '11–20',    from: dISO(11), to: dISO(Math.min(20, eom)) },
    { k: 'w3',      label: `21–${eom}`, from: dISO(21), to: dISO(eom) },
  ];
};

export default function SlipTrackerAuditPage() {
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const [status, setStatus] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [snapFrom, setSnapFrom] = useState('');
  const [snapTo, setSnapTo] = useState('');
  const [rangeFrom, setRangeFrom] = useState(() => todayISO().slice(0, 8) + '01');
  const [rangeTo, setRangeTo] = useState(() => todayISO().slice(0, 8) + '10');
  const [includeOverdue, setIncludeOverdue] = useState(true);
  const [hideZeroDue, setHideZeroDue] = useState(false);
  const [excludeCt, setExcludeCt] = useState([]);
  const [excludeSp, setExcludeSp] = useState(['Deb']);  // Deb excluded by default; user can uncheck via picker
  const [groupBy, setGroupBy] = useState('salesperson_name');
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [detailRow, setDetailRow] = useState(null);         // clicked row for modal
  const [execOpen, setExecOpen] = useState(false);          // executive summary modal
  const [explainerOpen, setExplainerOpen] = useState(false);
  const [snapConfirmOpen, setSnapConfirmOpen] = useState(false);
  const [snapTaking, setSnapTaking] = useState(false);
  // Guided tour state — null means closed, 0..N-1 = current step
  const [tourStep, setTourStep] = useState(null);
  // Pre-loaded picker options from the latest snapshot — so the Exclude
  // Salesperson / Client type dropdowns are usable BEFORE the user has
  // clicked Run audit. Without this the defaults show "1 selected · No
  // options" which looks broken.
  const [preloadedOpts, setPreloadedOpts] = useState({ salespersons: [], client_types: [] });
  const [zohoOrgId, setZohoOrgId] = useState('');
  useEffect(() => {
    api.get('/books/metadata').then((r) => setZohoOrgId(r.data?.zoho_org_id || '')).catch(() => {});
  }, []);
  const [snapSearchA, setSnapSearchA] = useState('');
  const [snapSearchB, setSnapSearchB] = useState('');
  const [reasonFilter, setReasonFilter] = useState([]);   // selected reason keys

  const loadStatus = useCallback(async () => {
    try {
      // Ensure today's snapshot exists so business-owner workflow
      // (compare "yesterday snapshot" vs "today live") works at any hour of
      // the day, not only after the 14:00 IST cron. Backend gates this to
      // admin-only and returns 403 for others — we silently swallow that so
      // non-admins simply work off the last cron-produced snapshot.
      try { await api.post('/books/slip-tracker/snapshots/take'); }
      catch (_) { /* 403 for non-admin, or already exists — non-fatal */ }
      const [s, l] = await Promise.all([
        api.get('/books/slip-tracker/status'),
        api.get('/books/slip-tracker/snapshots'),
      ]);
      setStatus(s.data);
      setSnapshots(l.data.snapshots || []);
      if (!snapFrom && !snapTo && (l.data.snapshots || []).length >= 2) {
        setSnapTo(l.data.snapshots[0]);      // today (freshest)
        setSnapFrom(l.data.snapshots[1]);    // yesterday
      } else if (!snapTo && (l.data.snapshots || []).length === 1) {
        setSnapTo(l.data.snapshots[0]);
      }
    } catch (e) { console.error(e); }
  }, [snapFrom, snapTo]);

  const takeSnapshotNow = useCallback(async () => {
    setSnapTaking(true);
    try {
      const r = await api.post('/books/slip-tracker/snapshots/take');
      toast.success(`Today's snapshot refreshed — ${r.data.invoices} invoices captured live`);
      setSnapTo(r.data.snapshot_date);
      setSnapConfirmOpen(false);
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to take snapshot');
    } finally { setSnapTaking(false); }
  }, [loadStatus]);

  // Info about today's existing snapshot — populated when the confirm popup opens
  const [snapInfo, setSnapInfo] = useState(null);
  useEffect(() => {
    if (!snapConfirmOpen) { setSnapInfo(null); return; }
    (async () => {
      try {
        const r = await api.get(`/books/slip-tracker/snapshots/for/${todayISO()}`);
        setSnapInfo(r.data);
      } catch (_) { setSnapInfo({ exists: false }); }
    })();
  }, [snapConfirmOpen]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Pre-load salesperson + client-type options from the latest snapshot so
  // the exclude pickers work before the first audit run.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/books/slip-tracker/filter-options');
        setPreloadedOpts(r.data || { salespersons: [], client_types: [] });
      } catch (_) { /* endpoint may 403 for non-admin viewers — non-fatal */ }
    })();
  }, []);

  // First-time onboarding — auto-launch the 60-sec tour once per browser.
  useEffect(() => {
    if (!audit) return;
    if (localStorage.getItem('stba_tour_seen_v1')) return;
    // Delay a tick so refs are laid out before we position the tooltip
    const t = setTimeout(() => setTourStep(0), 500);
    return () => clearTimeout(t);
  }, [audit]);
  useEffect(() => {
    if (!status?.backfill?.running) return;
    const id = setInterval(loadStatus, 3000);
    return () => clearInterval(id);
  }, [status?.backfill?.running, loadStatus]);

  const runAudit = useCallback(async () => {
    if (!snapFrom || !snapTo) { toast.error('Pick both snapshot dates'); return; }
    if (!rangeFrom || !rangeTo) { toast.error('Pick a date range'); return; }
    setLoading(true);
    try {
      const params = {
        snap_from: snapFrom, snap_to: snapTo,
        range_from: rangeFrom, range_to: rangeTo,
        include_overdue: includeOverdue, hide_zero_due: hideZeroDue,
        group_by: groupBy,
      };
      if (excludeCt.length) params.exclude_ct = excludeCt.join(',');
      if (excludeSp.length) params.exclude_sp = excludeSp.join(',');
      const r = await api.get('/books/slip-tracker/audit', { params });
      setAudit(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Audit failed');
      setAudit(null);
    } finally { setLoading(false); }
  }, [snapFrom, snapTo, rangeFrom, rangeTo, includeOverdue, hideZeroDue, excludeCt, excludeSp, groupBy]);

  useEffect(() => { if (snapFrom && snapTo) runAudit(); },
    [snapFrom, snapTo, rangeFrom, rangeTo, includeOverdue, hideZeroDue, groupBy, excludeCt.length, excludeSp.length]);  // eslint-disable-line

  const takeSnapshot = async () => {
    setRunning(true);
    try {
      const r = await api.post('/books/slip-tracker/snapshots/take');
      toast.success(`Snapshot for ${r.data.snapshot_date}: ${r.data.invoices} rows`);
      loadStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Snapshot failed'); }
    finally { setRunning(false); }
  };

  const printSummary = () => {
    if (!audit) return;
    const k = audit.kpis;

    // ── Aggregate global evidence: payments, credit notes, expected-date moves
    // Walk every classified row (left/entered/changed) once so the report
    // reflects every ₹ that moved between the two snapshots.
    const paymentsAgg = []; // {inv, customer, amount, date, sp, ct}
    const cnAgg = [];       // {inv, customer, amount, date, sp, ct}
    const dateMoves = [];   // {inv, customer, from, to, actor, at, reason, sp}
    const dateChangeCount = { moved: 0, moved_in: 0, moved_out: 0 };
    let totalPaymentsAmt = 0, totalCnAmt = 0;
    let paymentsCount = 0, cnCount = 0;

    (audit.groups || []).forEach(g => {
      ['left', 'entered', 'changed'].forEach(bucket => {
        (g[bucket] || []).forEach(r => {
          const before = r.before || r;
          const evi = r.evidence || {};
          const invNo = before.invoice_number || '';
          const cust = before.customer_name || '';
          const sp = before.salesperson_name || '';
          const ct = before.client_type || '';
          (evi.payments || []).forEach(p => {
            paymentsAgg.push({ inv: invNo, customer: cust, amount: p.amount, date: p.date, sp, ct });
            totalPaymentsAmt += Number(p.amount || 0);
            paymentsCount += 1;
          });
          (evi.credit_notes || []).forEach(c => {
            cnAgg.push({ inv: invNo, customer: cust, amount: c.amount, date: c.date, cn: c.creditnote_number, sp, ct });
            totalCnAmt += Number(c.amount || 0);
            cnCount += 1;
          });
          (evi.comments || []).forEach(c => {
            dateMoves.push({
              inv: invNo, customer: cust, sp,
              from: r.before?.expected_payment_date || evi.from_date || '',
              to: c.new_expected_date, actor: c.actor, at: c.changed_at || c.changed_on,
              reason: c.reason || '',
            });
          });
          if (r.reason === 'expected_date_moved') dateChangeCount.moved += 1;
          if (r.reason === 'expected_date_moved_in') dateChangeCount.moved_in += 1;
          if (r.reason === 'expected_date_moved_out') dateChangeCount.moved_out += 1;
        });
      });
    });

    // Sort by ₹ impact
    paymentsAgg.sort((a, b) => Number(b.amount) - Number(a.amount));
    cnAgg.sort((a, b) => Number(b.amount) - Number(a.amount));
    // Sort date moves by ₹ impact of the parent invoice — falling back to time
    dateMoves.sort((a, b) => (a.at || '').localeCompare(b.at || '') * -1);

    const topPayments = paymentsAgg.slice(0, 25);
    const topCns = cnAgg.slice(0, 25);
    const topMoves = dateMoves.slice(0, 25);

    const totMovements = audit.counts.left + audit.counts.entered + audit.counts.changed;
    const netDeltaDue = k.expected_due.delta;
    const netDeltaPromised = k.promised.delta;

    // Human-language executive summary
    const bDate = audit.snap_to, aDate = audit.snap_from;
    const dirDue = netDeltaDue < 0 ? 'dropped' : netDeltaDue > 0 ? 'grew' : 'stayed flat at';
    const dirPromised = netDeltaPromised < 0 ? 'shrank' : netDeltaPromised > 0 ? 'grew' : 'stayed flat at';
    // Use KPI-level deltas as the headline figures so the summary matches
    // the KPI strip exactly. Evidence-level sums may under-count when Zoho
    // records a balance drop without a linked payment (edge case).
    const kpiPaymentDelta = k.received.delta;
    const kpiCnDelta = (k.received_cn && k.received_cn.delta) || 0;
    const execLines = [
      `Between <b>${aDate}</b> and <b>${bDate}</b> the tracked window ${audit.range_from} → ${audit.range_to} moved on <b>${totMovements}</b> invoices (${audit.counts.left} left, ${audit.counts.entered} entered, ${audit.counts.changed} changed; ${audit.counts.unchanged} unchanged).`,
      `Cash-in-hand from actual payments grew by <b class="pos">${fmtInr(kpiPaymentDelta)}</b>${paymentsCount ? ` (linked evidence: ${paymentsCount} receipt${paymentsCount === 1 ? '' : 's'})` : ''}. Credit-note offsets ${kpiCnDelta === 0 ? 'were unchanged' : `moved by <b class="vio">${fmtInr(kpiCnDelta)}</b>`}${cnCount ? ` (linked evidence: ${cnCount} note${cnCount === 1 ? '' : 's'})` : ''}.`,
      `Expected Collection (Due) ${dirDue} <b>${fmtInr(Math.abs(netDeltaDue))}</b>; Promised ${dirPromised} <b>${fmtInr(Math.abs(netDeltaPromised))}</b>.`,
      `Expected dates were moved on <b>${dateChangeCount.moved + dateChangeCount.moved_in + dateChangeCount.moved_out}</b> invoices — ${dateChangeCount.moved_in} pulled a promise into the window, ${dateChangeCount.moved_out} pushed one out, and ${dateChangeCount.moved} shifted while staying in scope.`,
    ];

    const fmtDDMon = (iso) => {
      if (!iso) return '—';
      try { return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }); }
      catch { return iso; }
    };

    const kpi = (label, a, b, delta, sub, tone) => `
      <div class="kpi tone-${tone || 'slate'}">
        <div class="lab">${label}</div>
        <div class="val">${fmtInr(a)} <span class="arw">→</span> ${fmtInr(b)}</div>
        <div class="dl ${delta > 0 ? 'pos' : delta < 0 ? 'neg' : 'flat'}">
          ${delta === 0 ? 'No change' : (delta > 0 ? '▲ ' : '▼ ') + fmtInr(Math.abs(delta))}
        </div>
        ${sub ? `<div class="sub">${sub}</div>` : ''}
      </div>`;

    const paymentsTable = topPayments.length ? `
      <h2>Top actual payments (${paymentsCount} total · ${fmtInr(totalPaymentsAmt)})</h2>
      <table class="rows">
        <thead><tr><th>Date</th><th>Invoice</th><th>Customer</th><th>Salesperson</th><th class="ralign">Amount</th></tr></thead>
        <tbody>
          ${topPayments.map(p => `<tr>
            <td>${fmtDDMon(p.date)}</td>
            <td><b>${p.inv || '—'}</b></td>
            <td>${p.customer || '—'}</td>
            <td class="muted">${p.sp || '—'}</td>
            <td class="ralign pos"><b>${fmtInr(p.amount)}</b></td>
          </tr>`).join('')}
        </tbody>
        <tfoot><tr>
          <td colspan="4" class="ralign"><b>Shown ${topPayments.length} of ${paymentsCount}</b></td>
          <td class="ralign pos"><b>${fmtInr(topPayments.reduce((a, p) => a + Number(p.amount || 0), 0))}</b></td>
        </tr></tfoot>
      </table>` : '';

    const cnTable = topCns.length ? `
      <h2>Top credit notes applied (${cnCount} total · ${fmtInr(totalCnAmt)})</h2>
      <p class="note">Credit notes are accounting offsets — <b>not cash</b>. Listed separately from payments for clarity.</p>
      <table class="rows">
        <thead><tr><th>Applied on</th><th>CN #</th><th>Invoice</th><th>Customer</th><th>Salesperson</th><th class="ralign">Amount</th></tr></thead>
        <tbody>
          ${topCns.map(c => `<tr>
            <td>${fmtDDMon(c.date)}</td>
            <td><b>${c.cn || '—'}</b></td>
            <td>${c.inv || '—'}</td>
            <td>${c.customer || '—'}</td>
            <td class="muted">${c.sp || '—'}</td>
            <td class="ralign vio"><b>${fmtInr(c.amount)}</b></td>
          </tr>`).join('')}
        </tbody>
        <tfoot><tr>
          <td colspan="5" class="ralign"><b>Shown ${topCns.length} of ${cnCount}</b></td>
          <td class="ralign vio"><b>${fmtInr(topCns.reduce((a, c) => a + Number(c.amount || 0), 0))}</b></td>
        </tr></tfoot>
      </table>` : '';

    const movesTable = topMoves.length ? `
      <h2>Key expected-date changes (${dateMoves.length} events)</h2>
      <p class="note">Latest promise moves first · attribution captured from Zoho comment history.</p>
      <table class="rows">
        <thead><tr><th>When</th><th>Invoice</th><th>Customer</th><th>Actor</th><th>Moved to</th><th>Reason</th></tr></thead>
        <tbody>
          ${topMoves.map(m => `<tr>
            <td class="muted">${m.at || '—'}</td>
            <td><b>${m.inv || '—'}</b></td>
            <td>${m.customer || '—'}</td>
            <td><b>${m.actor || '—'}</b></td>
            <td>${fmtDDMon(m.to)}</td>
            <td class="muted italic">${(m.reason || '').replace(/</g, '&lt;')}</td>
          </tr>`).join('')}
        </tbody>
      </table>` : '';

    // Full movements table (grouped) — condensed for the PDF
    const groupsHtml = audit.groups
      .filter(g => (g.left.length + g.entered.length + g.changed.length) > 0)
      .map(g => {
        const rows = [
          ...g.left.map(r => ({ tag: 'Left', tagCls: 'tag-out', r })),
          ...g.entered.map(r => ({ tag: 'Entered', tagCls: 'tag-in', r })),
          ...g.changed.map(r => ({ tag: 'Changed', tagCls: 'tag-mid', r })),
        ];
        if (!rows.length) return '';
        const line = ({ tag, tagCls, r }) => {
          const before = r.before || r;
          const after = r.after || r.new || r;
          const meta = REASON_META[r.reason] || REASON_META.unknown;
          const delta = tag === 'Changed'
            ? Number(before?.balance || 0) - Number(after?.balance || 0)
            : Number(after?.balance ?? before?.balance ?? 0);
          const deltaCls = tag === 'Changed'
            ? (delta > 0 ? 'pos' : delta < 0 ? 'neg' : 'muted')
            : (tag === 'Left' ? 'orn' : 'sky');
          const deltaTxt = tag === 'Changed'
            ? (delta > 0 ? '−' : '+') + fmtInr(Math.abs(delta))
            : (tag === 'Left' ? '↘ ' : '↗ ') + fmtInr(Math.abs(delta));
          return `<tr>
            <td><span class="tag ${tagCls}">${tag}</span></td>
            <td><b>${before.invoice_number || '—'}</b>${before.enrolment_no ? `<div class="muted xs">Enrol ${before.enrolment_no}</div>` : ''}</td>
            <td>${before.customer_name || '—'}</td>
            <td class="muted">${fmtDDMon(before.expected_payment_date)} → ${fmtDDMon(after.expected_payment_date)}</td>
            <td class="ralign ${deltaCls}"><b>${deltaTxt}</b></td>
            <td><span class="reason r-${r.reason}">${meta.label}</span></td>
          </tr>`;
        };
        return `<div class="grp">
          <h3>${g.label} <span class="delta ${g.delta_promised > 0 ? 'pos' : g.delta_promised < 0 ? 'neg' : 'muted'}">${g.delta_promised === 0 ? '—' : fmtDelta(g.delta_promised)}</span></h3>
          <div class="grp-meta">
            ${g.a_count} → ${g.b_count} inv ·
            Promised ${fmtInr(g.a_promised)} → ${fmtInr(g.b_promised)} ·
            Payments ${fmtInr(g.b_received)} · CN ${fmtInr(g.b_received_cn || 0)} ·
            Due ${fmtInr(g.b_expected)}
          </div>
          <table class="rows"><thead><tr><th style="width:70px">Bucket</th><th>Invoice</th><th>Customer</th><th>Expected A → B</th><th class="ralign">Impact</th><th>Reason</th></tr></thead>
            <tbody>${rows.map(line).join('')}</tbody>
          </table>
        </div>`;
      }).join('');

    const html = `<!doctype html><html><head>
      <meta charset="utf-8"/>
      <title>Slip Tracker Audit · ${audit.snap_from} → ${audit.snap_to}</title>
      <style>
        * { box-sizing: border-box; }
        html, body { margin: 0; }
        body { font-family: -apple-system, "SF Pro Text", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
               color: #1e293b; background: #ffffff; padding: 32px 40px; max-width: 900px; margin: auto;
               font-size: 12px; line-height: 1.5; }
        .doc-head { border-bottom: 3px solid #0f172a; padding-bottom: 12px; margin-bottom: 18px;
                    display: flex; align-items: flex-start; justify-content: space-between; }
        .doc-head h1 { font-size: 22px; margin: 0 0 4px 0; letter-spacing: -0.3px; color: #0f172a; }
        .doc-head .sub { color: #475569; font-size: 12px; }
        .stamp { text-align: right; color: #64748b; font-size: 10px; }
        .stamp .l { font-weight: 700; color: #0f172a; letter-spacing: 1.5px; text-transform: uppercase; font-size: 9px; }
        .exec { background: #f8fafc; border-left: 4px solid #4f46e5; padding: 12px 16px; border-radius: 4px;
                margin: 0 0 20px 0; }
        .exec .lab { font-size: 9px; font-weight: 800; letter-spacing: 2px; color: #4f46e5;
                     text-transform: uppercase; margin-bottom: 6px; }
        .exec p { margin: 3px 0; color: #334155; font-size: 12px; }
        .kpis { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; margin: 0 0 22px 0; }
        .kpi { border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px; background: #fff; }
        .kpi.tone-sky { border-color: #bae6fd; background: linear-gradient(to bottom right, #f0f9ff, #ffffff); }
        .kpi.tone-emerald { border-color: #a7f3d0; background: linear-gradient(to bottom right, #ecfdf5, #ffffff); }
        .kpi.tone-violet { border-color: #ddd6fe; background: linear-gradient(to bottom right, #f5f3ff, #ffffff); }
        .kpi.tone-amber { border-color: #fde68a; background: linear-gradient(to bottom right, #fffbeb, #ffffff); }
        .kpi.tone-rose { border-color: #fecaca; background: linear-gradient(to bottom right, #fef2f2, #ffffff); }
        .kpi .lab { text-transform: uppercase; font-size: 8px; color: #334155; font-weight: 800; letter-spacing: 1.2px; }
        .kpi .val { font-weight: 600; font-size: 11px; color: #0f172a; margin-top: 4px; }
        .kpi .val .arw { color: #94a3b8; margin: 0 3px; font-weight: 300; }
        .kpi .dl { font-weight: 800; font-size: 14px; margin-top: 4px; letter-spacing: -0.3px; }
        .kpi .dl.pos { color: #059669; } .kpi .dl.neg { color: #dc2626; } .kpi .dl.flat { color: #94a3b8; }
        .kpi .sub { font-size: 9px; color: #64748b; margin-top: 3px; }
        h2 { font-size: 13px; margin: 24px 0 6px 0; padding-bottom: 4px; color: #0f172a;
             border-bottom: 1px solid #cbd5e1; letter-spacing: -0.2px; font-weight: 700; }
        h3 { font-size: 12px; margin: 14px 0 3px 0; color: #334155; display: flex; justify-content: space-between;
             align-items: baseline; font-weight: 700; }
        h3 .delta { font-size: 11px; font-weight: 700; }
        h3 .delta.pos { color: #059669; } h3 .delta.neg { color: #dc2626; } h3 .delta.muted { color: #94a3b8; }
        .grp { break-inside: avoid; margin-bottom: 14px; }
        .grp-meta { font-size: 10px; color: #64748b; margin-bottom: 4px; }
        table.rows { width: 100%; border-collapse: collapse; font-size: 10.5px; margin-bottom: 6px; }
        table.rows th { background: #f1f5f9; padding: 6px 8px; text-align: left; border-bottom: 2px solid #cbd5e1;
                        font-size: 9px; font-weight: 700; color: #334155; text-transform: uppercase; letter-spacing: 0.5px; }
        table.rows td { padding: 5px 8px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
        table.rows tfoot td { background: #f8fafc; font-size: 10px; }
        .ralign { text-align: right; }
        .muted { color: #64748b; } .xs { font-size: 9px; } .italic { font-style: italic; }
        .pos { color: #059669; } .neg { color: #dc2626; } .orn { color: #ea580c; } .sky { color: #0284c7; } .vio { color: #7c3aed; }
        .note { font-size: 10px; color: #64748b; margin: 2px 0 6px 0; }
        .tag { display: inline-block; padding: 1px 6px; border-radius: 10px; border: 1px solid; font-size: 9px;
               font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
        .tag-out { color: #b91c1c; border-color: #fecaca; background: #fef2f2; }
        .tag-in  { color: #047857; border-color: #a7f3d0; background: #ecfdf5; }
        .tag-mid { color: #b45309; border-color: #fde68a; background: #fffbeb; }
        .reason { display: inline-block; padding: 1px 6px; border-radius: 10px; border: 1px solid #cbd5e1;
                  background: #f8fafc; color: #475569; font-size: 9px; font-weight: 600; }
        .r-payment_received  { color: #047857; border-color: #a7f3d0; background: #ecfdf5; }
        .r-credit_note_applied { color: #7c3aed; border-color: #ddd6fe; background: #f5f3ff; }
        .r-settled_mixed { color: #0f766e; border-color: #99f6e4; background: #f0fdfa; }
        .r-expected_date_moved,
        .r-expected_date_moved_in,
        .r-expected_date_moved_out { color: #b45309; border-color: #fde68a; background: #fffbeb; }
        .r-invoice_voided { color: #dc2626; border-color: #fecaca; background: #fef2f2; }
        .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #e2e8f0;
                  text-align: center; color: #94a3b8; font-size: 9px; }
        @page { size: A4; margin: 12mm; }
        @media print {
          body { padding: 8px; max-width: none; }
          .grp, .kpis { page-break-inside: avoid; }
          h2 { page-break-after: avoid; }
        }
      </style></head><body>
      <div class="doc-head">
        <div>
          <h1>Slip Tracker · Audit Report</h1>
          <div class="sub">
            Snapshot <b>${audit.snap_from}</b> <span style="color:#94a3b8">vs</span> <b>${audit.snap_to}</b>
            &nbsp;·&nbsp; Range <b>${audit.range_from} → ${audit.range_to}</b>
          </div>
          <div class="sub" style="margin-top:3px">
            Include overdue: <b>${audit.filters.include_overdue ? 'Yes' : 'No'}</b>
            &nbsp;·&nbsp; Excluded SP: <b>${(audit.filters.exclude_sp || []).join(', ') || 'None'}</b>
            &nbsp;·&nbsp; Excluded CT: <b>${(audit.filters.exclude_ct || []).join(', ') || 'None'}</b>
          </div>
        </div>
        <div class="stamp">
          <div class="l">Generated</div>
          <div>${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST</div>
        </div>
      </div>

      <div class="exec">
        <div class="lab">Executive summary</div>
        ${execLines.map(l => `<p>${l}</p>`).join('')}
      </div>

      <div class="kpis">
        ${kpi('Promised', k.promised.a, k.promised.b, k.promised.delta, `${k.promised.a_count} → ${k.promised.b_count} inv`, 'sky')}
        ${kpi('Actual payments', k.received.a, k.received.b, k.received.delta, 'Cash-in-hand', 'emerald')}
        ${kpi('Credit notes', k.received_cn?.a || 0, k.received_cn?.b || 0, k.received_cn?.delta || 0, 'Accounting offset', 'violet')}
        ${kpi('Expected due', k.expected_due.a, k.expected_due.b, k.expected_due.delta, 'Balance still open', 'amber')}
        ${kpi('Slipped', k.slipped.a, k.slipped.b, k.slipped.delta, 'Promise shifted later', 'rose')}
      </div>

      ${paymentsTable}
      ${cnTable}
      ${movesTable}

      ${groupsHtml ? `<h2>Full movement log</h2>${groupsHtml}` : ''}

      <div class="footer">
        Slip Tracker Audit · ${audit.counts.left} left · ${audit.counts.entered} entered · ${audit.counts.changed} changed · ${audit.counts.unchanged} unchanged<br/>
        Powered by Zoho Books snapshot diff · Confidential — internal use only
      </div>
    </body></html>`;

    const w = window.open('', '_blank');
    if (!w) { toast.error('Popup blocked — allow popups for this site'); return; }
    w.document.write(html); w.document.close();
    setTimeout(() => w.print(), 500);
  };

  // Options for exclude pickers — fall back to preloaded snapshot lists
  // when the audit hasn't been run yet, so pickers are usable from the start.
  const [ctOpts, spOpts] = useMemo(() => {
    const cts = new Set(), sps = new Set();
    (audit?.groups || []).forEach((g) => {
      ['left', 'entered', 'changed'].forEach((b) => {
        (g[b] || []).forEach((r) => {
          const src = r.before || r;
          if (src.client_type) cts.add(src.client_type);
          if (src.salesperson_name) sps.add(src.salesperson_name);
        });
      });
    });
    // Fallback to preloaded snapshot options if audit hasn't populated yet
    if (cts.size === 0) (preloadedOpts.client_types || []).forEach((v) => cts.add(v));
    if (sps.size === 0) (preloadedOpts.salespersons || []).forEach((v) => sps.add(v));
    return [Array.from(cts).sort(), Array.from(sps).sort()];
  }, [audit, preloadedOpts]);

  const exportCsv = () => {
    if (!audit) return;
    const rows = [['Group', 'Bucket', 'Invoice #', 'Customer', 'Salesperson', 'Client Type',
                   'Before Expected', 'After Expected', 'Before Balance', 'After Balance',
                   'Reason', 'Payments', 'CN', 'Date Move Notes']];
    audit.groups.forEach((g) => {
      const emit = (bucket, arr) => (arr || []).forEach((r) => {
        const before = r.before || r;
        const after = r.after || r.new || r;
        const evidence = r.evidence || {};
        const pmts = (evidence.payments || []).map(p => `${p.payment_number}:${p.amount}:${p.date}`).join('|');
        const cns = (evidence.credit_notes || []).map(c => `${c.creditnote_number}:${c.amount}:${c.date}`).join('|');
        const comments = (evidence.comments || []).map(c => `${c.changed_at || c.changed_on}·${c.actor}·${c.new_expected_date}·${(c.reason || '').replace(/[\r\n]+/g, ' ')}`).join('|');
        rows.push([g.label, bucket, before.invoice_number, before.customer_name,
                   before.salesperson_name, before.client_type,
                   before.expected_payment_date, after.expected_payment_date,
                   before.balance, after.balance, r.reason, pmts, cns, comments]);
      });
      emit('left', g.left);
      emit('entered', g.entered);
      emit('changed', g.changed);
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replaceAll('"', '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `slip-tracker-audit_${audit.snap_from}_vs_${audit.snap_to}_${audit.range_from}-${audit.range_to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleGroup = (k) => setExpandedGroups((s) => ({ ...s, [k]: !s[k] }));
  const toggleRow = (k) => setExpandedRows((s) => ({ ...s, [k]: !s[k] }));

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="slip-tracker-audit-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800 flex items-center gap-2">
            Slip Tracker — Audit
            <button type="button" onClick={() => setExplainerOpen(true)} data-testid="stba-explainer-btn"
              className="inline-flex items-center gap-1 text-[10px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-1 hover:bg-indigo-100 transition"
              title="Explain KPI math, snapshot logic and how to read the A→B diff">
              <Info className="w-3 h-3" /> How this maths
            </button>
            <button type="button" onClick={() => setTourStep(0)} data-testid="stba-tour-btn"
              className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 hover:bg-emerald-100 transition"
              title="60-second guided tour — first-time onboarding">
              <PlayCircle className="w-3 h-3" /> Watch in 60 sec
            </button>
          </h1>
          <p className="text-sm text-slate-500 mt-0.5 max-w-3xl">
            <b>What changed?</b> Pick a <b>date range</b> for promises (e.g. this month) and two dates to compare — usually
            <b> yesterday vs today</b>. This page shows every invoice that <b className="text-sky-700">newly appeared</b>,
            <b className="text-orange-700"> was cleared / removed</b>, or <b className="text-amber-700">changed</b> between those two dates, and <b>why</b> the total moved.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <AuditToolsMenu onExportCsv={exportCsv} onPrintPdf={printSummary} audit={audit}
            isAdmin={isAdmin} eventsTotal={status?.events_total || 0}
            backfillRunning={status?.backfill?.running} loadStatus={loadStatus} />
        </div>
      </div>

      {status?.backfill?.running && (
        <Card className="p-3 border-amber-200 bg-amber-50 text-[12px] text-amber-800 flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" />
          Background <b>{status.backfill.phase}</b> backfill running — {status.backfill.progress}/{status.backfill.total || '?'}
        </Card>
      )}
      {status && (
        <div className="text-[11px] text-slate-500">
          {status.snapshots_total_rows.toLocaleString('en-IN')} snapshot rows · earliest {status.earliest_snapshot || '—'} · latest {status.latest_snapshot || '—'} · {status.events_total.toLocaleString('en-IN')} expected-date events
        </div>
      )}

      {/* ── Filter bar (scrolls with page) ────────────────────── */}
      <Card className="p-4 border space-y-3 bg-white shadow-sm relative z-40" data-tour="filters">
        {/* Beginner-friendly banner explaining the 2-date mental model */}
        <div className="text-[11px] rounded-md border border-indigo-200 bg-indigo-50/60 px-2.5 py-1.5 text-indigo-900">
          <b>How to use this page:</b> Choose the <b>same date range for expected payments</b>, then two snapshot dates (usually <i>yesterday</i> vs <i>today</i>).
          The audit shows every invoice that changed between those two snapshots — and why the totals moved.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-3 text-[11px]">
          <SnapPicker label="Compare FROM (before)" value={snapFrom} options={snapshots}
            search={snapSearchA} setSearch={setSnapSearchA} onChange={setSnapFrom}
            testid="stba-snap-from" />
          <SnapPicker label="Compare TO (after)" value={snapTo} options={snapshots}
            search={snapSearchB} setSearch={setSnapSearchB} onChange={setSnapTo}
            testid="stba-snap-to" />
          <label className="flex flex-col gap-1">
            <span className="text-slate-500 font-semibold" title="Only invoices with an expected payment date in this window are audited">Expected payments FROM</span>
            <input type="date" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)}
              className="border rounded px-2 py-1" data-testid="stba-range-from" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-slate-500 font-semibold">Expected payments TO</span>
            <input type="date" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)}
              className="border rounded px-2 py-1" data-testid="stba-range-to" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-slate-500 font-semibold">Group by</span>
            <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}
              className="border rounded px-2 py-1" data-testid="stba-group-by">
              <option value="salesperson_name">Salesperson</option>
              <option value="cf_client_type">Client type</option>
              <option value="customer_name">Customer</option>
              <option value="expected_payment_date">Expected date</option>
            </select>
          </label>
          <div className="flex items-end gap-1">
            {isAdmin && (
              <Button size="sm" variant="outline"
                onClick={() => {
                  // If a snapshot for today already exists (auto-run at 14:00 IST),
                  // warn the admin before overwriting. Otherwise proceed silently.
                  const todaySnap = snapshots.find((s) => s === todayISO());
                  if (todaySnap) { setSnapConfirmOpen(true); }
                  else { takeSnapshotNow(); }
                }}
                title="Force-refresh today's snapshot from live Zoho. Admins only. Auto-run happens daily at 14:00 IST."
                data-testid="stba-refresh-live">
                <RefreshCw className="w-3 h-3 mr-1" /> Take snapshot now
              </Button>
            )}
            <Button size="sm" onClick={runAudit} disabled={loading} className="flex-1" data-testid="stba-run">
              {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Run audit
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          <span className="text-slate-500">Preset:</span>
          {PRESETS(rangeFrom, rangeTo).map((p) => (
            <button key={p.k} onClick={() => { setRangeFrom(p.from); setRangeTo(p.to); }}
              className={`px-2 py-0.5 rounded border ${rangeFrom === p.from && rangeTo === p.to ? 'bg-slate-800 text-white' : 'bg-white hover:bg-slate-50'}`}
              data-testid={`stba-preset-${p.k}`}>{p.label}</button>
          ))}
          <span className="mx-2 text-slate-200">·</span>
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={includeOverdue} onChange={(e) => setIncludeOverdue(e.target.checked)}
              className="accent-violet-600" data-testid="stba-include-overdue" />
            Include overdue
          </label>
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={hideZeroDue} onChange={(e) => setHideZeroDue(e.target.checked)}
              className="accent-violet-600" data-testid="stba-hide-zero" />
            Hide 0 due
          </label>
          <label className="flex items-center gap-1 ml-auto" title="Show a small '· N unchanged' hint next to each group row. The full unchanged list is always accessible via the 'Unchanged' tab inside each expanded group.">
            <input type="checkbox" checked={showUnchanged} onChange={(e) => setShowUnchanged(e.target.checked)}
              className="accent-violet-600" data-testid="stba-show-unchanged" />
            Show unchanged hint
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <MultiPicker label="Exclude Client type" options={ctOpts} value={excludeCt} onChange={setExcludeCt}
            testid="stba-exclude-ct" />
          <MultiPicker label="Exclude Salesperson" options={spOpts} value={excludeSp} onChange={setExcludeSp}
            testid="stba-exclude-sp" />
        </div>
      </Card>

      {/* ── KPI diff strip (sticky so it's always visible on scroll) ──── */}
      {audit && (
        <>
          <div className="flex items-center justify-end mt-2 mb-1">
            <Button
              size="sm" variant="outline"
              onClick={() => setExecOpen(true)}
              className="h-7 gap-1.5 border-violet-300 text-violet-700 hover:bg-violet-50"
              data-testid="stba-exec-summary-btn"
            >
              <Info className="w-3.5 h-3.5" /> Executive Summary
            </Button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 sticky top-0 z-30 bg-slate-50/95 backdrop-blur py-2 -mx-4 md:-mx-6 px-4 md:px-6 border-b border-slate-200"
             data-tour="kpis">
          <KpiCard label="Promised" a={audit.kpis.promised.a} b={audit.kpis.promised.b}
            delta={audit.kpis.promised.delta} suba={`${audit.kpis.promised.a_count} inv`}
            subb={`${audit.kpis.promised.b_count} inv`} accent="sky" />
          <KpiCard label="Actual payments" a={audit.kpis.received.a} b={audit.kpis.received.b}
            delta={audit.kpis.received.delta} accent="emerald" positiveGood
            hint="Cash-in-hand — payments dated inside the range window" />
          <KpiCard label="Credit notes applied" a={audit.kpis.received_cn?.a || 0} b={audit.kpis.received_cn?.b || 0}
            delta={audit.kpis.received_cn?.delta || 0} accent="violet"
            hint="Accounting offset (not cash) — CN apply-date inside range" />
          <KpiCard label="Expected Collection (Due)" a={audit.kpis.expected_due.a} b={audit.kpis.expected_due.b}
            delta={audit.kpis.expected_due.delta} accent="amber" negativeGood />
          <KpiCard label="Slipped promises" a={audit.kpis.slipped.a} b={audit.kpis.slipped.b}
            delta={audit.kpis.slipped.delta} accent="rose" negativeGood />
        </div>
        </>
      )}

      {/* Reason filter chips (multi-select) */}
      {audit && (
        <div data-tour="reasons">
          <ReasonFilterBar audit={audit} value={reasonFilter} onChange={setReasonFilter} />
        </div>
      )}


      {/* ── Group table ────────────────────────────── */}
      {audit ? (
        <Card className="p-0 border overflow-hidden mt-2" data-tour="groups">
          {/* Collapse/Expand-all control */}
          <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50 border-b text-[11px]">
            <span className="font-semibold text-slate-600">Groups ({audit.groups.length})</span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setExpandedGroups(Object.fromEntries(audit.groups.map(g => [g.key, true])))}
                className="px-2 py-0.5 rounded border border-slate-200 hover:bg-white text-slate-600 text-[10px] font-semibold"
                data-testid="stba-expand-all"
              >Expand all</button>
              <button
                type="button"
                onClick={() => { setExpandedGroups({}); setExpandedRows({}); }}
                className="px-2 py-0.5 rounded border border-slate-200 hover:bg-white text-slate-600 text-[10px] font-semibold"
                data-testid="stba-collapse-all"
              >Collapse all</button>
            </div>
          </div>
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="p-2 text-left">Group</th>
                <th className="p-2 text-right">A count → B</th>
                <th className="p-2 text-right">A Promised</th>
                <th className="p-2 text-right">B Promised</th>
                <th className="p-2 text-right">Δ Promised</th>
                <th className="p-2 text-left">Why (counts)</th>
              </tr>
            </thead>
            <tbody>
              {audit.groups
                .filter(g => {
                  if (!reasonFilter.length) return true;
                  const total = [...(g.left || []), ...(g.entered || []), ...(g.changed || [])]
                    .filter(r => reasonFilter.includes(r.reason)).length;
                  return total > 0;
                })
                .map((g) => {
                const open = expandedGroups[g.key];
                return (
                  <React.Fragment key={g.key}>
                    <tr className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => toggleGroup(g.key)}
                        data-testid={`stba-group-${g.key}`}>
                      <td className="p-2 font-semibold flex items-center gap-1">
                        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        {g.label}
                        {showUnchanged && <span className="text-slate-400 font-normal ml-2">· {g.unchanged_count} unchanged</span>}
                      </td>
                      <td className="p-2 text-right">
                        <span className="text-slate-500">{g.a_count}</span>
                        <ArrowRight className="w-3 h-3 inline mx-1 text-slate-300" />
                        <span className={g.delta_count > 0 ? 'text-emerald-700' : g.delta_count < 0 ? 'text-rose-700' : ''}>
                          {g.b_count}
                        </span>
                        {g.delta_count !== 0 && <span className="ml-1 text-slate-400 text-[9px]">({g.delta_count > 0 ? '+' : ''}{g.delta_count})</span>}
                      </td>
                      <td className="p-2 text-right">{fmtInr(g.a_promised)}</td>
                      <td className="p-2 text-right">{fmtInr(g.b_promised)}</td>
                      <td className={`p-2 text-right font-semibold ${g.delta_promised > 0 ? 'text-emerald-700' : g.delta_promised < 0 ? 'text-rose-700' : 'text-slate-400'}`}>
                        {g.delta_promised === 0 ? '—' : fmtDelta(g.delta_promised)}
                      </td>
                      <td className="p-2">
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(g.why || {})
                            .filter(([k, v]) => v > 0 && k !== 'unchanged')
                            .map(([k, v]) => {
                              const meta = REASON_META[k] || REASON_META.unknown;
                              return (
                                <span key={k} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full border text-[9px] ${meta.cls}`}>
                                  <meta.Icon className="w-2.5 h-2.5" />
                                  {meta.label} {v}
                                </span>
                              );
                            })}
                        </div>
                      </td>
                    </tr>
                    {open && (
                      <tr className="bg-slate-50/60">
                        <td colSpan={6} className="p-3">
                          <GroupDetail g={g} expandedRows={expandedRows} onToggle={toggleRow}
                            reasonFilter={reasonFilter}
                            onRowClick={(r, bucket) => setDetailRow({ row: r, bucket, group: g.label,
                              snapA: audit.snap_from, snapB: audit.snap_to })} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {audit.groups.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-slate-400">No matching invoices in the two snapshots.</td></tr>
              )}
            </tbody>
          </table>
          <div className="p-2 bg-slate-50 border-t text-[11px] text-slate-500 flex flex-wrap gap-4">
            <span><b>{audit.counts.left}</b> left</span>
            <span><b>{audit.counts.entered}</b> entered</span>
            <span><b>{audit.counts.changed}</b> changed</span>
            <span><b>{audit.counts.unchanged}</b> unchanged</span>
          </div>
        </Card>
      ) : (
        <Card className="p-6 text-center text-slate-400 text-sm">
          {loading ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : null}
          Pick two snapshots + a date range to see the full audit.
        </Card>
      )}

      {/* ── Executive Summary modal ──────────────────────────── */}
      <ExecutiveSummaryModal
        open={execOpen}
        onOpenChange={setExecOpen}
        audit={audit}
        snapFrom={snapFrom}
        snapTo={snapTo}
        rangeFrom={rangeFrom}
        rangeTo={rangeTo}
        onOpenInvoice={(r) => {
          // Reconstruct a minimal detailRow so the unified drill-down modal opens.
          // We don't know the exact bucket the offender came from — 'changed' is
          // the safest default because the modal only reads invoice_id from us.
          if (!r?.invoice_id) return;
          setExecOpen(false);
          setDetailRow({
            row: { invoice_id: r.invoice_id, invoice_number: r.invoice_number,
                   customer_name: r.customer, salesperson_name: r.salesperson,
                   reason: r.reason },
            bucket: r.source || 'changed',
            group: r.salesperson,
            snapA: snapFrom, snapB: snapTo,
          });
        }}
      />


      {/* ── Row detail modal (unified with Overview) ─────────────── */}
      <SlipInvoiceDetailModal
        open={!!detailRow}
        onOpenChange={(o) => { if (!o) setDetailRow(null); }}
        invoiceId={detailRow?.row?.invoice_id}
        zohoOrgId={zohoOrgId}
        snapshotDiff={detailRow ? (() => {
          const r = detailRow.row || {};
          // Bucket-specific pairing:
          //   changed  → row.before, row.after
          //   left     → row itself is snapshot A; row.new is snap B (may be null)
          //   entered  → row.old is snap A (may be null); row itself is snap B
          const before = detailRow.bucket === 'changed' ? (r.before || {})
                       : detailRow.bucket === 'entered' ? (r.old || {})
                       : r; // left
          const after  = detailRow.bucket === 'changed' ? (r.after || {})
                       : detailRow.bucket === 'left'    ? (r.new || {})
                       : r; // entered
          return {
            snap_from: detailRow.snapA,
            snap_to: detailRow.snapB,
            bucket: detailRow.bucket,
            group: detailRow.group,
            reason: r.reason,
            before, after,
            delta_balance: Number(after?.balance || 0) - Number(before?.balance || 0),
          };
        })() : null}
      />

      {/* ── 60-second guided tour ─────────────────────────────── */}
      <Tour
        open={tourStep !== null}
        step={tourStep}
        onStep={setTourStep}
        onClose={() => setTourStep(null)}
        storageKey="stba_tour_seen_v1"
        steps={[
          { selector: '[data-tour="filters"]',
            title: 'Pick the same range, two snapshot dates',
            body: 'Set the expected-payment window (e.g. this month) and two snapshot dates to compare — usually yesterday vs today. Then click Run audit.' },
          { selector: '[data-tour="kpis"]',
            title: 'The 5 KPIs: A → B → Δ',
            body: 'Each card shows the Before value, After value, and the change. Promised = Payments + Due. Credit Notes are shown separately because they are not cash.' },
          { selector: '[data-tour="reasons"]',
            title: 'Filter by reason',
            body: 'Every changed invoice is stamped with why it moved — payment received, credit note applied, promise date shifted, etc. Click a chip to keep only those rows.' },
          { selector: '[data-tour="groups"]',
            title: 'Drill into any group',
            body: 'Expand a salesperson (or client type / customer) row to see Left, Entered, Changed and Unchanged buckets. Click any invoice to open the full detail with money + student + history.' },
          { selector: '[data-testid="stba-explainer-btn"]',
            title: "Any time you're unsure",
            body: "Tap 'How this maths' at the top for a plain-English explanation of every number on this page. You can also re-open this tour any time with the ▶ button next to it." },
        ]}
      />

      {/* ── Take-snapshot confirm dialog (admin only) ────────── */}      <Dialog open={snapConfirmOpen} onOpenChange={setSnapConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="stba-snap-confirm">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-amber-600" /> Take another snapshot for today?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-[12.5px] text-slate-700">
            {snapInfo === null ? (
              <p className="text-slate-400 italic">Checking existing snapshot…</p>
            ) : snapInfo?.exists ? (
              <>
                <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2">
                  <p><b>Today's snapshot already exists.</b></p>
                  <p className="text-[11px] text-slate-600 mt-1">
                    Captured on <b>{snapInfo.captured_at ? new Date(snapInfo.captured_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : 'unknown time'}</b> IST
                    {snapInfo.reconstructed ? ' (reconstructed)' : ' (live)'}<br/>
                    {snapInfo.invoice_count?.toLocaleString('en-IN')} invoices captured.
                  </p>
                </div>
                <p className="text-[11.5px] text-slate-600">
                  The daily auto-run at <b>14:00 IST</b> normally handles this. Taking another snapshot now will <b>overwrite</b>
                  today's numbers with whatever Zoho currently shows. Any comparisons you save may look different afterwards.
                </p>
              </>
            ) : (
              <p>No snapshot yet for today. Proceed to capture one now?</p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button type="button" onClick={() => setSnapConfirmOpen(false)}
              className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"
              data-testid="stba-snap-cancel">Cancel</button>
            <button type="button" onClick={takeSnapshotNow} disabled={snapTaking}
              className="text-[11px] px-3 py-1.5 rounded-md bg-amber-600 text-white hover:bg-amber-700 flex items-center gap-1 disabled:opacity-50"
              data-testid="stba-snap-confirm-yes">
              {snapTaking && <Loader2 className="w-3 h-3 animate-spin" />}
              Yes, overwrite
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── How-this-maths explainer popup ──────────────────────── */}
      <Dialog open={explainerOpen} onOpenChange={setExplainerOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="stba-explainer-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Info className="w-4 h-4 text-indigo-600" /> How the Audit maths works
            </DialogTitle>
            <p className="text-[11px] text-slate-500 mt-0.5">Read the A → B diff with confidence. Every KPI, tab and reason is defined below.</p>
          </DialogHeader>

          <div className="space-y-4 text-[12px] text-slate-700 leading-relaxed">
            <section>
              <h3 className="font-semibold text-slate-900 mb-1.5 flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-indigo-500" /> What is a snapshot?
              </h3>
              <p>
                A snapshot is a frozen photocopy of every invoice's state (balance, expected date, status, salesperson, client type) taken at <b>14:00 IST daily</b>. Click <span className="font-mono text-[11px] bg-slate-100 rounded px-1 py-0.5">Live</span> to force-capture today's state on demand. The Audit picks two snapshots — <b>Snapshot A</b> (earlier) and <b>Snapshot B</b> (later) — and diffs every invoice inside your chosen <b>Range from → Range to</b> window.
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-slate-900 mb-1.5 flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" /> The core identity (same as Overview)
              </h3>
              <div className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 font-mono text-[13px] text-emerald-900 tabular-nums">
                Promised &nbsp;=&nbsp; Actual&nbsp;Payments &nbsp;+&nbsp; Expected&nbsp;Due
              </div>
              <p className="text-[11px] text-slate-600 mt-1.5">
                Every KPI card shows <b>A → B</b>. The delta below tells you how much that number moved between the two snapshots.
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-slate-900 mb-1.5 flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-500" /> Credit Notes are <b>not cash</b>
              </h3>
              <p>
                A separate KPI so you can spot when a balance drop came from a CN offset instead of a real payment. Never add CN to "Actual Payments" — that would double-count against Promised.
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-slate-900 mb-1.5 flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-sky-500" /> Reading the group table
              </h3>
              <ul className="space-y-1.5">
                <li><b className="text-orange-700">Left</b> — invoice was in scope at A but not at B (paid off, voided, expected-date moved out, reclassified, or salesperson reassigned).</li>
                <li><b className="text-sky-700">Entered</b> — invoice appeared at B but wasn't at A (new invoice, expected-date moved in, or reclassified into scope).</li>
                <li><b className="text-amber-700">Changed</b> — invoice present in both snapshots but something moved (balance, expected date, status, salesperson, or client type).</li>
                <li><b className="text-slate-600">Unchanged</b> — present in both, no change. Shown for verification only; doesn't affect deltas.</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-slate-900 mb-1.5 flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-500" /> Reason attribution
              </h3>
              <p>
                Each moved invoice is stamped with a <b>Why</b> — <span className="font-mono text-[10px] bg-slate-100 rounded px-1">payment_received</span>, <span className="font-mono text-[10px] bg-slate-100 rounded px-1">credit_note_applied</span>, <span className="font-mono text-[10px] bg-slate-100 rounded px-1">expected_date_moved</span>, etc — derived from Zoho payments, credit notes and comment history. Click any row to see full evidence (payment IDs, CN numbers, actor names, comment timeline).
              </p>
              <p className="text-[11px] text-slate-600 mt-1.5">
                <b>Zoho reconciliation lag:</b> if a CN was applied before Snap A but Zoho updated the balance later, we fall back to the lifetime CN flag so the row still reads "CN applied" instead of a generic "Balance ↓".
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-slate-900 mb-1.5 flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" /> Why totals may differ from <b>Billing vs Collections</b>
              </h3>
              <p>Same as Overview:</p>
              <ol className="list-decimal ml-5 space-y-1 mt-1">
                <li>Only invoices with an <span className="font-mono text-[11px] bg-slate-100 rounded px-1">Expected Payment Date</span> set in Zoho appear here. Fill it or run <b>Backfill originals</b> to include the rest.</li>
                <li>Promised is post-CN; Billing is gross. Gross ≈ <span className="font-mono">Promised + CN Applied</span>.</li>
              </ol>
              {audit && (
                <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 mt-2 text-[11.5px] font-mono tabular-nums text-slate-800">
                  <div className="flex justify-between"><span>Snap B — Promised</span><span>{fmtInr(audit.kpis.promised.b)}</span></div>
                  <div className="flex justify-between"><span>+ Credit Notes Applied</span><span className="text-violet-700">{fmtInr(audit.kpis.received_cn?.b || 0)}</span></div>
                  <div className="flex justify-between border-t border-slate-300 mt-1 pt-1 font-bold"><span>≈ Gross billing of tracked inv</span><span>{fmtInr(audit.kpis.promised.b + (audit.kpis.received_cn?.b || 0))}</span></div>
                </div>
              )}
            </section>
          </div>

          <div className="pt-3 border-t border-slate-100 flex justify-end">
            <button type="button" onClick={() => setExplainerOpen(false)}
              className="text-[11px] px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
              data-testid="stba-explainer-close">Got it</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── KPI card ───────────────────────────────────────
const ACCENT = {
  sky:      { bg: 'from-sky-50 to-sky-100/60 border-sky-200', label: 'text-sky-700' },
  emerald:  { bg: 'from-emerald-50 to-emerald-100/60 border-emerald-200', label: 'text-emerald-700' },
  amber:    { bg: 'from-amber-50 to-amber-100/60 border-amber-200', label: 'text-amber-700' },
  rose:     { bg: 'from-rose-50 to-rose-100/60 border-rose-200', label: 'text-rose-700' },
  violet:   { bg: 'from-violet-50 to-violet-100/60 border-violet-200', label: 'text-violet-700' },
};

const KpiCard = ({ label, a, b, delta, suba, subb, accent, positiveGood, negativeGood, hint }) => {
  const good = (delta > 0 && positiveGood) || (delta < 0 && negativeGood);
  const bad = (delta < 0 && positiveGood) || (delta > 0 && negativeGood);
  const dColor = delta === 0 ? 'text-slate-400' : good ? 'text-emerald-700' : bad ? 'text-rose-700' : 'text-slate-600';
  const acc = ACCENT[accent] || ACCENT.sky;
  return (
    <Card className={`p-3 border bg-gradient-to-br ${acc.bg}`}>
      <div className={`text-[10px] font-bold uppercase tracking-wide ${acc.label}`}>{label}</div>
      <div className="mt-1 flex items-center gap-2">
        <div>
          <div className="text-[10px] text-slate-400">A</div>
          <div className="text-base font-semibold">{fmtInr(a)}</div>
          {suba && <div className="text-[9px] text-slate-500">{suba}</div>}
        </div>
        <ArrowRight className="w-3 h-3 text-slate-300" />
        <div>
          <div className="text-[10px] text-slate-400">B</div>
          <div className="text-base font-semibold">{fmtInr(b)}</div>
          {subb && <div className="text-[9px] text-slate-500">{subb}</div>}
        </div>
      </div>
      <div className={`mt-1 text-sm font-bold ${dColor}`}>
        {delta === 0 ? 'No change' : fmtDelta(delta)}
      </div>
      {hint && <div className="text-[9px] text-slate-500 mt-0.5 leading-tight">{hint}</div>}
    </Card>
  );
};

// ─── Group detail (drill-in) ───────────────────────
const BucketBadge = ({ label, count, cls }) => (
  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full border text-[9px] ${cls}`}>
    <b className="mr-1">{count}</b> {label}
  </span>
);

const GroupDetail = ({ g, expandedRows, onToggle, onRowClick, reasonFilter = [] }) => {
  const filterRows = (rows) => reasonFilter.length ? rows.filter(r => reasonFilter.includes(r.reason)) : rows;
  const [tab, setTab] = useState(g.left.length ? 'left' : g.changed.length ? 'changed' : g.entered.length ? 'entered' : 'unchanged');
  const buckets = [
    { k: 'left',      label: `Left (${filterRows(g.left).length})`,             cls: 'bg-rose-100 text-rose-700 border-rose-300', rows: filterRows(g.left) },
    { k: 'entered',   label: `Entered (${filterRows(g.entered).length})`,       cls: 'bg-emerald-100 text-emerald-700 border-emerald-300', rows: filterRows(g.entered) },
    { k: 'changed',   label: `Changed (${filterRows(g.changed).length})`,       cls: 'bg-amber-100 text-amber-700 border-amber-300', rows: filterRows(g.changed) },
    { k: 'unchanged', label: `Unchanged (${(g.unchanged || []).length})`,       cls: 'bg-slate-100 text-slate-600 border-slate-300', rows: (g.unchanged || []) },
  ];
  return (
    <div className="space-y-2">
      <div className="flex gap-1">
        {buckets.map((b) => (
          <button key={b.k} onClick={() => setTab(b.k)}
            className={`px-2 py-0.5 rounded border text-[10px] ${tab === b.k ? 'bg-slate-800 text-white border-slate-800' : 'bg-white hover:bg-slate-50'}`}
            data-testid={`stba-tab-${g.key}-${b.k}`}>
            {b.label}
          </button>
        ))}
      </div>
      <div className="border rounded bg-white overflow-hidden">
        <table className="w-full text-[10px]">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="p-1 text-left">Invoice / Customer</th>
              <th className="p-1 text-left">Before → After</th>
              <th className="p-1 text-right">
                {tab === 'left' ? 'Amount left' : tab === 'entered' ? 'Amount entered' : tab === 'unchanged' ? 'Balance' : 'Balance Δ'}
              </th>
              <th className="p-1 text-left">Reason</th>
            </tr>
          </thead>
          <tbody>
            {(buckets.find((b) => b.k === tab)?.rows || []).map((r, i) => {
              // Backend shapes:
              //   left    → top-level = A slim, r.new = B slim
              //   entered → top-level = B slim, r.old = A slim
              //   changed → r.before = A slim, r.after = B slim
              const before = r.before || (tab === 'entered' ? (r.old || r) : r);
              const after = r.after || r.new || (tab === 'entered' ? r : null) || r;
              const meta = REASON_META[r.reason] || REASON_META.unknown;
              const key = `${g.key}_${tab}_${r.invoice_id}`;
              const open = expandedRows[key];
              const hasCn = before?.has_cn || after?.has_cn;
              let amtValue = 0, amtCls = 'text-slate-400', amtPrefix = '';
              if (tab === 'left') {
                amtValue = Number(after?.balance ?? before?.balance ?? 0);
                amtCls = 'text-orange-600'; amtPrefix = '↘ ';
              } else if (tab === 'entered') {
                amtValue = Number(after?.balance ?? before?.balance ?? 0);
                amtCls = 'text-sky-600'; amtPrefix = '↗ ';
              } else if (tab === 'unchanged') {
                amtValue = Number(r?.balance ?? before?.balance ?? 0);
                amtCls = 'text-slate-600'; amtPrefix = '';
              } else {
                const drop = Number(before?.balance || 0) - Number(after?.balance || 0);
                amtValue = drop;
                amtCls = drop > 0 ? 'text-emerald-700' : drop < 0 ? 'text-rose-700' : 'text-slate-400';
                amtPrefix = drop > 0 ? '−' : drop < 0 ? '+' : '';
              }
              return (
                <React.Fragment key={key}>
                  <tr className="border-t">
                    <td className="p-1 cursor-pointer" onClick={() => onRowClick && onRowClick(r, tab)}
                        data-testid={`stba-row-${before.invoice_number}`}>
                      <div className="font-semibold text-violet-700 underline decoration-dotted flex items-center gap-1">
                        {before.invoice_number}
                        {hasCn && (
                          <span className="inline-flex items-center gap-0.5 px-1 py-[1px] rounded border bg-violet-100 text-violet-700 border-violet-300 text-[8px] font-normal"
                            title="A credit note has been applied to this invoice at some point">
                            <Receipt className="w-2.5 h-2.5" /> CN
                          </span>
                        )}
                      </div>
                      <div className="text-slate-500">{before.customer_name}</div>
                      {before.enrolment_no && <div className="text-slate-400 text-[9px]">Enrol {before.enrolment_no}</div>}
                    </td>
                    <td className="p-1 cursor-pointer" onClick={() => onRowClick && onRowClick(r, tab)}>
                      {tab === 'changed' && r.changed_fields?.length ? (
                        <div className="space-y-0.5">
                          {r.changed_fields.map((cf, k) => (
                            <div key={k} className="text-[9px]">
                              <span className="text-slate-500">{cf.label}:</span>{' '}
                              <span className="text-rose-600">{typeof cf.from === 'number' ? fmtInr(cf.from) : (cf.from || '—')}</span>
                              <ArrowRight className="w-2.5 h-2.5 inline mx-1 text-slate-300" />
                              <span className="text-emerald-700">{typeof cf.to === 'number' ? fmtInr(cf.to) : (cf.to || '—')}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-[9px]">
                          <span className="text-slate-500">exp {fmtDate(before.expected_payment_date)}</span>
                          <ArrowRight className="w-2.5 h-2.5 inline mx-1 text-slate-300" />
                          <span>{fmtDate(after.expected_payment_date)}</span>
                          <div className="text-slate-400">bal {fmtInr(before.balance)} → {fmtInr(after.balance)}</div>
                        </div>
                      )}
                    </td>
                    <td className={`p-1 text-right font-semibold cursor-pointer ${amtCls}`}
                        onClick={() => onRowClick && onRowClick(r, tab)}>
                      {amtValue === 0 ? '—' : `${amtPrefix}${fmtInr(Math.abs(amtValue))}`}
                    </td>
                    <td className="p-1">
                      <span onClick={() => onToggle(key)}
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[9px] cursor-pointer ${meta.cls}`}>
                        <meta.Icon className="w-3 h-3" />
                        {meta.label}
                        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                      </span>
                    </td>
                  </tr>
                  {open && (
                    <tr className="bg-slate-50">
                      <td colSpan={4} className="p-2 text-[10px] text-slate-600">
                        <EvidenceBlock r={r} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {(buckets.find((b) => b.k === tab)?.rows || []).length === 0 && (
              <tr><td colSpan={4} className="p-3 text-center text-slate-400">Nothing in this bucket</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Evidence block ────────────────────────────────
const EvidenceBlock = ({ r }) => {
  const e = r.evidence || {};
  return (
    <div className="space-y-1">
      {e.payments?.length ? (
        <div>
          <b>Payments applied between snapshots:</b>
          <ul className="list-disc pl-5">
            {e.payments.map((p, i) => (
              <li key={i}>{p.payment_number} — {fmtInr(p.amount)} on {p.date}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {e.credit_notes?.length ? (
        <div>
          <b>Credit notes applied:</b>
          <ul className="list-disc pl-5">
            {e.credit_notes.map((c, i) => (
              <li key={i}>{c.creditnote_number} — {fmtInr(c.amount)} on {c.date}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {e.from_date && e.to_date ? (
        <div>
          <b>Expected date moved:</b> {fmtDate(e.from_date)} → {fmtDate(e.to_date)}
        </div>
      ) : null}
      {e.comments?.length ? (
        <div className="pl-2 border-l-2 border-violet-300 mt-1">
          <b>Zoho reason comments:</b>
          <ul className="list-disc pl-5 mt-0.5 space-y-0.5">
            {e.comments.map((c, i) => (
              <li key={i}>
                <span className="text-slate-500">{c.changed_at || c.changed_on}</span>
                {' · '}<b>{c.actor}</b>
                {' moved to '}{fmtDate(c.new_expected_date)}
                {c.reason && <span className="italic text-slate-600"> — "{c.reason}"</span>}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {!e.payments && !e.credit_notes && !e.comments && !e.from_date && (
        <div className="text-slate-400 italic">No additional evidence captured.</div>
      )}
    </div>
  );
};

// ─── Multi-select picker ───────────────────────────
const MultiPicker = ({ label, options, value, onChange, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <div className="text-slate-500 font-semibold mb-1 text-[11px]">{label}</div>
      <button onClick={() => setOpen((o) => !o)}
        className="w-full border rounded px-2 py-1 text-left flex items-center justify-between text-[11px]"
        data-testid={testid}>
        <span>{value.length ? `${value.length} selected` : 'None'}</span>
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-56 overflow-auto bg-white border rounded shadow p-1">
          {options.length === 0 && <div className="text-slate-400 text-[10px] p-2">No options — run an audit first.</div>}
          {options.map((o) => (
            <label key={o} className="flex items-center gap-1 py-0.5 px-1 cursor-pointer hover:bg-slate-50"
              data-testid={`${testid}-opt-${o}`}>
              <input type="checkbox" checked={value.includes(o)} className="accent-violet-600"
                onChange={(e) => onChange(e.target.checked ? [...value, o] : value.filter((x) => x !== o))} />
              <span className="text-[10px]">{o}</span>
            </label>
          ))}
          <div className="flex justify-between border-t mt-1 pt-1">
            <button onClick={() => onChange([])} className="text-[10px] text-slate-500 hover:underline">Clear</button>
            <button onClick={() => setOpen(false)} className="text-[10px] text-violet-600 hover:underline">Done</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Searchable Snapshot picker ────────────────────
const SnapPicker = ({ label, value, options, search, setSearch, onChange, testid }) => {
  const [open, setOpen] = useState(false);
  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [search, options]);
  return (
    <label className="flex flex-col gap-1 relative">
      <span className="text-slate-500 font-semibold">{label}</span>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="border rounded px-2 py-1 flex items-center justify-between text-left"
        data-testid={testid}>
        <span>{value || '— pick —'}</span>
        <ChevronDown className="w-3 h-3 text-slate-400" />
      </button>
      {open && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 border rounded bg-white shadow-lg max-h-72 overflow-hidden flex flex-col">
          <div className="flex items-center gap-1 border-b px-2 py-1 bg-slate-50">
            <Search className="w-3 h-3 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search date…" autoFocus
              className="text-[11px] outline-none flex-1 bg-transparent"
              data-testid={`${testid}-search`} />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 && <div className="p-2 text-[10px] text-slate-400">No matches</div>}
            {filtered.map((d) => (
              <button key={d} onClick={() => { onChange(d); setOpen(false); setSearch(''); }}
                className={`w-full text-left px-2 py-1 text-[11px] hover:bg-violet-50 ${d === value ? 'bg-violet-100 text-violet-700 font-semibold' : ''}`}
                data-testid={`${testid}-opt-${d}`}>
                {d}
              </button>
            ))}
          </div>
        </div>
      )}
    </label>
  );
};

// ─── Row-detail modal ──────────────────────────────
const InvoiceAuditDetailModal = ({ open, onClose, row, bucket, group, snapA, snapB }) => {
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (!open || !row?.invoice_id) { setDetail(null); return; }
    setDetailLoading(true);
    api.get('/books/slip-tracker/audit/invoice-detail', { params: { invoice_id: row.invoice_id } })
      .then(r => setDetail(r.data))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  }, [open, row?.invoice_id]);

  if (!row) return null;
  const before = row.before || row;
  const after = row.after || row.new || row;
  const meta = REASON_META[row.reason] || REASON_META.unknown;
  const enrol = before.enrolment_no || after.enrolment_no;
  const delta = row.delta_balance ?? row.delta_amount ?? 0;

  const openStudent = () => {
    if (!enrol) return;
    window.open(`/training/students?focus=${encodeURIComponent(enrol)}`, '_blank');
  };

  const summary = (() => {
    if (bucket === 'entered') {
      const fromExp = before?.expected_payment_date;
      const toExp = after?.expected_payment_date;
      if (fromExp && toExp && fromExp !== toExp)
        return `This invoice entered the tracker window because its Expected date moved from ${fmtDate(fromExp)} → ${fmtDate(toExp)}.`;
      return `This invoice appeared in the tracker window at ${snapB}.`;
    }
    if (bucket === 'left') {
      switch (row.reason) {
        case 'payment_received':      return `Cleared by customer payment between ${snapA} and ${snapB}.`;
        case 'credit_note_applied':   return `Cleared by credit note between ${snapA} and ${snapB}.`;
        case 'settled_mixed':         return `Cleared by a mix of payment + credit note between ${snapA} and ${snapB}.`;
        case 'write_off':             return `Cleared by write-off between ${snapA} and ${snapB}.`;
        case 'balance_cleared':       return `Balance dropped to zero between ${snapA} and ${snapB}.`;
        case 'invoice_voided':        return `Invoice was voided between ${snapA} and ${snapB}.`;
        case 'expected_date_moved_out': {
          const fd = row.evidence?.from_date, td = row.evidence?.to_date;
          return `Left the window because Expected date moved${fd ? ` from ${fmtDate(fd)}` : ''}${td ? ` → ${fmtDate(td)}` : ''}.`;
        }
        default:                       return `Left the tracker window between ${snapA} and ${snapB}.`;
      }
    }
    if (bucket === 'changed') {
      const first = row.changed_fields?.[0];
      if (row.reason === 'payment_received')      return `Customer paid ${fmtInr(delta)} between ${snapA} and ${snapB}.`;
      if (row.reason === 'credit_note_applied')   return `Credit note of ${fmtInr(delta)} applied between ${snapA} and ${snapB}.`;
      if (row.reason === 'settled_mixed')         return `Balance dropped by ${fmtInr(delta)} via payment + credit note.`;
      if (row.reason === 'expected_date_moved')   return `Expected date moved ${first ? `from ${fmtDate(first.from)} to ${fmtDate(first.to)}` : ''} — still inside the window.`;
      if (row.reason === 'reassigned_salesperson') return `Salesperson changed${first ? `: ${first.from} → ${first.to}` : ''}.`;
      if (row.reason === 'reclassified_client_type') return `Client type changed${first ? `: ${first.from} → ${first.to}` : ''}.`;
      if (row.reason === 'invoice_voided')        return `Invoice was voided.`;
      return `${row.changed_fields?.length || 0} field${row.changed_fields?.length === 1 ? '' : 's'} changed between ${snapA} and ${snapB}.`;
    }
    return '';
  })();

  const hasEvidence = row.evidence?.payments?.length || row.evidence?.credit_notes?.length ||
                      row.evidence?.comments?.length || row.evidence?.from_date;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2 flex-wrap">
            <span className="text-slate-500 text-[11px] font-normal">{group} · {bucket}</span>
            <span className="font-semibold">{before.invoice_number}</span>
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-normal ${meta.cls}`}>
              <meta.Icon className="w-3 h-3" /> {meta.label}
            </span>
            {delta !== 0 && (
              <span className={`text-[11px] font-semibold ${bucket === 'changed' ? (delta > 0 ? 'text-emerald-700' : 'text-rose-700') : (bucket === 'entered' ? 'text-sky-600' : 'text-orange-600')}`}>
                {bucket === 'left'    && `↘ ${fmtInr(Math.abs(delta))} left the window`}
                {bucket === 'entered' && `↗ ${fmtInr(Math.abs(delta))} entered the window`}
                {bucket === 'changed' && `Balance ${delta > 0 ? '−' : '+'}${fmtInr(Math.abs(delta))}`}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-[12px]">
          {/* TL;DR */}
          {summary && (
            <div className="text-[13px] text-slate-700 bg-slate-50 border rounded p-3 leading-snug">
              {summary}
            </div>
          )}

          {/* Customer strip */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] border-b pb-2">
            <span><span className="text-slate-400">Customer</span> <b>{before.customer_name || '—'}</b></span>
            {enrol && (
              <button className="text-violet-700 underline decoration-dotted flex items-center gap-1"
                      onClick={openStudent} data-testid="stba-modal-open-student">
                <span className="text-slate-400">Enrol</span> {enrol} <ExternalLink className="w-3 h-3" />
              </button>
            )}
            <span><span className="text-slate-400">SP</span> <b>{before.salesperson_name || '—'}</b></span>
            <span><span className="text-slate-400">CT</span> <b>{before.client_type || '—'}</b></span>
            <span><span className="text-slate-400">Invoice</span> <b>{fmtInr(before.invoice_total || after.invoice_total || 0)}</b></span>
          </div>

          {/* Changed fields — only render when there ARE changes */}
          {row.changed_fields?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Changed fields</div>
              <table className="w-full text-[11px] border rounded overflow-hidden">
                <thead className="bg-slate-50 text-slate-500 text-[10px]">
                  <tr>
                    <th className="p-1.5 text-left">Field</th>
                    <th className="p-1.5 text-left">Snapshot A · {snapA}</th>
                    <th className="p-1.5 text-left">Snapshot B · {snapB}</th>
                  </tr>
                </thead>
                <tbody>
                  {row.changed_fields.map((cf, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-1.5 text-slate-600">{cf.label}</td>
                      <td className="p-1.5 text-rose-600">{typeof cf.from === 'number' ? fmtInr(cf.from) : (cf.from || '—')}</td>
                      <td className="p-1.5 text-emerald-700">{typeof cf.to === 'number' ? fmtInr(cf.to) : (cf.to || '—')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Snapshot A vs B — always visible so users see the full picture */}
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Snapshot comparison</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <SnapshotCard title={`Snapshot A · ${snapA}`} row={before} otherRow={after} />
              <SnapshotCard title={`Snapshot B · ${snapB}`} row={after} otherRow={before} />
            </div>
          </div>

          {/* Invoice details + Zoho link */}
          {detail?.invoice && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1 flex items-center gap-2">
                Invoice details
                {detail.invoice.zoho_url && (
                  <a href={detail.invoice.zoho_url} target="_blank" rel="noreferrer"
                    className="text-violet-700 underline decoration-dotted normal-case font-normal flex items-center gap-1"
                    data-testid="stba-modal-zoho-link">
                    Open in Zoho Books <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              <div className="border rounded p-2 bg-white grid grid-cols-2 md:grid-cols-4 gap-1 text-[11px]">
                <span className="text-slate-400">Invoice date</span><b>{fmtDate(detail.invoice.date)}</b>
                <span className="text-slate-400">Due date</span><b>{fmtDate(detail.invoice.due_date)}</b>
                <span className="text-slate-400">Expected</span><b>{fmtDate(detail.invoice.expected_payment_date)}</b>
                <span className="text-slate-400">Original expected</span><b>{fmtDate(detail.invoice.original_expected_payment_date)}</b>
                <span className="text-slate-400">Total</span><b>{fmtInr(detail.invoice.bcy_total || detail.invoice.total || 0)}</b>
                <span className="text-slate-400">Balance</span><b>{fmtInr(detail.invoice.bcy_balance ?? detail.invoke?.balance ?? 0)}</b>
                <span className="text-slate-400">Write-off</span><b>{fmtInr(detail.invoice.write_off_amount || 0)}</b>
                <span className="text-slate-400">Zoho status</span><b>{detail.invoice.status || '—'}</b>
              </div>
            </div>
          )}

          {/* Batch details */}
          {detail && (detail.batches?.length > 0 || detail.student) && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">
                Batch details {detail.student ? `· ${detail.student.name}` : ''}
              </div>
              {detail.batches?.length ? (
                <table className="w-full text-[11px] border rounded overflow-hidden">
                  <thead className="bg-slate-50 text-slate-500 text-[10px]">
                    <tr>
                      <th className="p-1.5 text-left">Batch #</th>
                      <th className="p-1.5 text-left">Batch name</th>
                      <th className="p-1.5 text-left">Trainer</th>
                      <th className="p-1.5 text-left">Type</th>
                      <th className="p-1.5 text-left">Dates</th>
                      <th className="p-1.5 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.batches.map((b, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-1.5 font-semibold">{b.batch_number || '—'}</td>
                        <td className="p-1.5">{b.batch_name || '—'}</td>
                        <td className="p-1.5">{b.trainer_name || '—'}</td>
                        <td className="p-1.5 text-slate-500">{b.batch_type || '—'}</td>
                        <td className="p-1.5 text-slate-500">{fmtDate(b.start_date)} → {fmtDate(b.end_date)}</td>
                        <td className="p-1.5"><span className="text-[9px] px-1 py-0.5 rounded border bg-slate-50">{b.batch_status || '—'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="border rounded p-2 bg-white text-[11px] text-slate-500">
                  Student found ({detail.student?.name || '—'}) but no batch enrolments yet.
                </div>
              )}
              {detail.student && (
                <div className="text-[10px] text-slate-500 mt-1">
                  Course: <b>{detail.student.course || '—'}</b> · Counsellor: <b>{detail.student.counsellor || '—'}</b> · Salesperson: <b>{before.salesperson_name || '—'}</b>
                </div>
              )}
            </div>
          )}

          {/* Payments applied (all history) */}
          {detail?.payments?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold mb-1">
                Payments applied ({detail.payments.length}) — cash-in-hand, moves to bank
              </div>
              <table className="w-full text-[11px] border rounded overflow-hidden">
                <thead className="bg-emerald-50 text-emerald-700 text-[10px]">
                  <tr><th className="p-1.5 text-left">Payment #</th><th className="p-1.5 text-left">Date</th><th className="p-1.5 text-left">Mode</th><th className="p-1.5 text-left">Reference</th><th className="p-1.5 text-right">Amount</th></tr>
                </thead>
                <tbody>
                  {detail.payments.map((p, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-1.5 font-semibold">{p.payment_number || '—'}</td>
                      <td className="p-1.5">{p.date || '—'}</td>
                      <td className="p-1.5">{p.mode || '—'}</td>
                      <td className="p-1.5 text-slate-500">{p.reference || '—'}</td>
                      <td className="p-1.5 text-right text-emerald-700 font-semibold">{fmtInr(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Credit notes applied (all history) — separate section, different colour */}
          {detail?.credit_notes?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-violet-700 font-semibold mb-1">
                Credit notes applied ({detail.credit_notes.length}) — NOT a payment; balance offset only
              </div>
              <table className="w-full text-[11px] border rounded overflow-hidden">
                <thead className="bg-violet-50 text-violet-700 text-[10px]">
                  <tr><th className="p-1.5 text-left">CN #</th><th className="p-1.5 text-left">Issued</th><th className="p-1.5 text-left">Applied on</th><th className="p-1.5 text-left">Reason</th><th className="p-1.5 text-right">Amount</th></tr>
                </thead>
                <tbody>
                  {detail.credit_notes.map((c, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-1.5 font-semibold">{c.creditnote_number || '—'}</td>
                      <td className="p-1.5">{c.cn_date || '—'}</td>
                      <td className="p-1.5">{c.apply_date || '—'}</td>
                      <td className="p-1.5 text-slate-500 truncate max-w-[280px]" title={c.reason || ''}>{c.reason || '—'}</td>
                      <td className="p-1.5 text-right text-violet-700 font-semibold">{fmtInr(c.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {detailLoading && (
            <div className="text-[11px] text-slate-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading invoice details, batches, payments & credit notes…</div>
          )}

          {/* Evidence — always show, with a friendly empty-state so users know we looked */}
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Evidence between snapshots</div>
            <div className="border rounded p-2 bg-white">
              {hasEvidence ? <EvidenceBlock r={row} /> : (
                <div className="text-slate-400 italic text-[11px]">
                  No payments, credit notes, or Zoho comments recorded between {snapA} and {snapB}.
                  {' '}This can happen when the change was purely a status flip or a field edit not backed by a transaction.
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const SnapshotCard = ({ title, row, otherRow }) => {
  const diff = (key) => {
    if (!otherRow) return false;
    const a = row?.[key], b = otherRow?.[key];
    if (typeof a === 'number' || typeof b === 'number')
      return Math.round(Number(a || 0) * 100) !== Math.round(Number(b || 0) * 100);
    return (a || '') !== (b || '');
  };
  const cell = (key, val) => (
    <b className={diff(key) ? 'text-amber-700 bg-amber-50 px-1 rounded' : ''}>{val}</b>
  );
  return (
    <Card className="p-3 border">
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">{title}</div>
      <div className="grid grid-cols-2 gap-1 text-[11px]">
        <span className="text-slate-400">Status</span>{cell('status', row?.status || '—')}
        <span className="text-slate-400">Expected date</span>{cell('expected_payment_date', fmtDate(row?.expected_payment_date))}
        <span className="text-slate-400">Original expected</span>{cell('original_expected_payment_date', fmtDate(row?.original_expected_payment_date))}
        <span className="text-slate-400">Slip days</span>{cell('slip_days', row?.slip_days ?? '—')}
        <span className="text-slate-400">Invoice total</span>{cell('invoice_total', fmtInr(row?.invoice_total || 0))}
        <span className="text-slate-400">Balance</span>{cell('balance', fmtInr(row?.balance || 0))}
        <span className="text-slate-400">Write-off</span>{cell('write_off_amount', fmtInr(row?.write_off_amount || 0))}
      </div>
    </Card>
  );
};


// ─── Reason filter chip bar ────────────────────────
const ALL_REASONS = [
  'payment_received', 'credit_note_applied', 'settled_mixed', 'balance_cleared',
  'expected_date_moved', 'expected_date_moved_in', 'expected_date_moved_out',
  'reassigned_salesperson', 'reclassified_client_type',
  'invoice_voided', 'write_off', 'new_invoice',
];

const ReasonFilterBar = ({ audit, value, onChange }) => {
  const counts = useMemo(() => {
    const c = {};
    (audit?.groups || []).forEach(g => {
      Object.entries(g.why || {}).forEach(([k, v]) => {
        if (k === 'unchanged') return;
        c[k] = (c[k] || 0) + (v || 0);
      });
    });
    return c;
  }, [audit]);
  const toggle = (k) => onChange(value.includes(k) ? value.filter(x => x !== k) : [...value, k]);
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[11px] px-1">
      <span className="text-slate-500 font-semibold mr-1">Filter by reason:</span>
      {ALL_REASONS.map((k) => {
        const meta = REASON_META[k] || REASON_META.unknown;
        const n = counts[k] || 0;
        const active = value.includes(k);
        const disabled = n === 0;
        return (
          <button key={k} onClick={() => !disabled && toggle(k)} disabled={disabled}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] transition
              ${active ? 'ring-2 ring-offset-1 ring-violet-400 ' + meta.cls : meta.cls}
              ${disabled ? 'opacity-30 cursor-not-allowed' : (active ? '' : 'opacity-70 hover:opacity-100')}`}
            data-testid={`stba-reason-filter-${k}`}
            title={disabled ? `No rows with reason "${meta.label}" in this comparison` : `Filter to only "${meta.label}"`}>
            <meta.Icon className="w-3 h-3" />
            {meta.label} <b>{n}</b>
          </button>
        );
      })}
      {value.length > 0 && (
        <button onClick={() => onChange([])}
          className="text-[10px] text-slate-500 hover:text-slate-800 flex items-center gap-1 ml-1"
          data-testid="stba-reason-filter-clear">
          <X className="w-3 h-3" /> Clear
        </button>
      )}
    </div>
  );
};


// ─── Compact Tools menu: Print PDF + Export CSV + Refresh comment history ─
const AuditToolsMenu = ({ onExportCsv, onPrintPdf, audit, isAdmin, eventsTotal, backfillRunning, loadStatus }) => {
  const [open, setOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const refreshCommentHistory = async () => {
    setOpen(false);
    setRefreshing(true);
    try {
      await api.post('/books/slip-tracker/events/backfill');
      toast.success("Comment history refresh queued — check the header count to see it grow (usually 5-15 min)");
      // Poll status a few times so the header count updates without a reload
      for (let i = 0; i < 4; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        await loadStatus?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to queue comment refresh');
    } finally { setRefreshing(false); }
  };

  return (
    <div className="relative">
      <Button size="sm" variant="outline" onClick={() => setOpen((v) => !v)} disabled={!audit}
        data-testid="stba-tools-menu">
        Tools <ChevronDown className="w-3 h-3 ml-1" />
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-50 mt-1 bg-white border border-slate-200 rounded-md shadow-lg py-1 min-w-[220px]">
            <button type="button" onClick={() => { setOpen(false); onPrintPdf(); }} data-testid="stba-print-summary"
              className="w-full text-left text-[11px] px-3 py-1.5 hover:bg-slate-50 flex items-center gap-2 text-rose-700">
              <FileText className="w-3 h-3" /> Print / PDF summary
            </button>
            <button type="button" onClick={() => { setOpen(false); onExportCsv(); }} data-testid="stba-export-csv"
              className="w-full text-left text-[11px] px-3 py-1.5 hover:bg-slate-50 flex items-center gap-2 text-emerald-700">
              <Download className="w-3 h-3" /> Export CSV
            </button>
            {isAdmin && (
              <>
                <div className="border-t border-slate-100 my-1" />
                <button type="button" onClick={refreshCommentHistory}
                  disabled={backfillRunning || refreshing}
                  data-testid="stba-refresh-events"
                  title="Re-pull invoice comment history from Zoho — needed if the events collection was empty after a fresh deploy. Admin only."
                  className="w-full text-left text-[11px] px-3 py-1.5 hover:bg-slate-50 flex items-center gap-2 text-slate-700 disabled:opacity-60">
                  <RefreshCw className={`w-3 h-3 ${refreshing || backfillRunning ? 'animate-spin' : ''}`} />
                  <span className="flex-1">
                    {backfillRunning ? 'Refreshing comment history…' : refreshing ? 'Queuing…' : 'Refresh comment history'}
                    <span className="block text-[9px] text-slate-400 leading-tight">
                      {eventsTotal.toLocaleString('en-IN')} events cached · pulls Zoho comment logs
                    </span>
                  </span>
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

